/**
 * 文件传输面板：发起、接受、下载。
 *
 * 老系统是点对点直传（打洞失败才回落服务端中转），NAT 后成功率很低，
 * Web 端更是完全无法参与。新系统统一走服务端中转 + 对象存储落地。
 */

import { useRef, useState } from 'react'

import { formatSize } from '../lib/adminApi'
import { assetURL } from '../lib/endpoint'
import { hasPerm, Perm, USER_ALL } from '../lib/protocol'
import { useFileStore, type Transfer } from '../stores/fileStore'
import { useRoomStore } from '../stores/roomStore'

// 传输列表与 file.* 监听都在 fileStore 里：这个面板随标签页挂载/卸载，
// 状态放在组件里的话，面板没打开时收不到邀约、切个标签进度就没了。
export function FileTransfer() {
  const items = useFileStore((s) => s.items)
  const sendFile = useFileStore((s) => s.send)
  const acceptFile = useFileStore((s) => s.accept)
  const cancelFile = useFileStore((s) => s.cancel)
  const [target, setTarget] = useState<number>(USER_ALL)
  const fileRef = useRef<HTMLInputElement>(null)

  const selfId = useRoomStore((s) => s.selfId)
  const members = useRoomStore((s) => s.members)
  const me = members.get(selfId)
  const canSend = !!me && hasPerm(me.prop, Perm.SendFile)

  const send = (file: File) => sendFile(file, target)
  const accept = (t: Transfer, ok: boolean) => acceptFile(t.fileId, ok)
  const cancel = (t: Transfer) => cancelFile(t.fileId)

  return (
    <div className="panel file-panel">
      <div className="panel-head">
        <span>文件传输</span>
        {items.length > 0 && <span className="badge">{items.length}</span>}
      </div>

      {canSend ? (
        <div className="file-send">
          <select value={target} onChange={(e) => setTarget(Number(e.target.value))}>
            <option value={USER_ALL}>发给所有人</option>
            {[...members.values()]
              .filter((m) => m.id !== selfId)
              .map((m) => (
                <option key={m.id} value={m.id}>
                  发给 {m.name || `席位 ${m.id}`}
                </option>
              ))}
          </select>
          <input
            ref={fileRef}
            type="file"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0]
              if (f) send(f)
              e.target.value = ''
            }}
          />
          <button onClick={() => fileRef.current?.click()}>选择文件</button>
        </div>
      ) : (
        <div className="file-nope">你当前没有传送文件的权限</div>
      )}

      <div className="file-list">
        {items.map((t) => (
          <div key={t.fileId} className={`file-item ${t.status}`}>
            <div className="file-meta">
              <span className="file-name" title={t.name}>
                {t.name}
              </span>
              <span className="dim">{formatSize(t.size)}</span>
            </div>
            <div className="file-sub">
              {t.outgoing
                ? `发给 ${t.destId === USER_ALL ? '所有人' : members.get(t.destId)?.name ?? t.destId}`
                : `来自 ${members.get(t.fromId)?.name ?? `席位 ${t.fromId}`}`}
              <span className="file-status">{statusText(t)}</span>
            </div>

            {(t.status === 'sending' || t.status === 'waiting') && (
              <div className="bar">
                <div className="bar-fill" style={{ width: `${t.progress * 100}%` }} />
              </div>
            )}

            <div className="file-ops">
              {!t.outgoing && t.status === 'offering' && (
                <>
                  <button className="mini primary" onClick={() => accept(t, true)}>
                    接收
                  </button>
                  <button className="mini" onClick={() => accept(t, false)}>
                    拒绝
                  </button>
                </>
              )}
              {t.status === 'done' && t.url && (
                <a className="mini-link" href={assetURL(t.url)} download={t.name}>
                  下载
                </a>
              )}
              {(t.status === 'sending' || t.status === 'waiting') && (
                <button className="mini" onClick={() => cancel(t)}>
                  取消
                </button>
              )}
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="chat-empty">暂无文件传输</div>}
      </div>
    </div>
  )
}

function statusText(t: Transfer): string {
  switch (t.status) {
    case 'offering':
      return '等待你确认'
    case 'waiting':
      return '等待对方接收'
    case 'sending':
      return `传输中 ${Math.round(t.progress * 100)}%`
    case 'done':
      return '已完成'
    case 'canceled':
      return '已取消'
    case 'failed':
      return `失败：${t.error ?? '未知原因'}`
    default:
      return ''
  }
}
